import React, { useState } from 'react';
import { 
  HelpCircle, Search, ChevronDown, ChevronUp, 
  ExternalLink, Mail, MessageCircle, BookOpen,
  Zap, TrendingUp, Award, Lightbulb, Sparkles,
  Target, FileText, Settings as SettingsIcon
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { helpContent, generalFaqs, quickTips } from './helpContent';

// Icon mapping for modules
const moduleIcons = {
  'dashboard': BookOpen,
  'bid-optimizer': TrendingUp,
  'quality-score': Award,
  'offer-analyzer': Lightbulb,
  'ai-generator': Sparkles,
  'campaign-builder': Target,
  'keyword-research': Search,
  'ad-creator': FileText,
  'settings': SettingsIcon
};

// ============================================
// FAQ ACCORDION
// ============================================
function FaqAccordion({ faqs, title }) {
  const { theme } = useTheme();
  const [openIndex, setOpenIndex] = useState(null);
  
  return (
    <div style={{ marginBottom: '32px' }}>
      {title && (
        <h3 style={{
          fontSize: '16px',
          fontWeight: '700',
          color: theme.colors.text.primary,
          margin: '0 0 16px 0'
        }}>
          {title}
        </h3>
      )}
      
      <div style={{
        background: theme.colors.bg.secondary,
        borderRadius: '12px',
        border: `1px solid ${theme.colors.border.primary}`,
        overflow: 'hidden'
      }}>
        {faqs.map((faq, index) => (
          <div 
            key={index}
            style={{
              borderBottom: index < faqs.length - 1 ? `1px solid ${theme.colors.border.primary}` : 'none'
            }}
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                textAlign: 'left'
              }}
            >
              <span style={{
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: '500',
                flex: 1
              }}>
                {faq.question}
              </span>
              {openIndex === index ? (
                <ChevronUp size={18} style={{ color: theme.colors.text.tertiary, flexShrink: 0 }} />
              ) : (
                <ChevronDown size={18} style={{ color: theme.colors.text.tertiary, flexShrink: 0 }} />
              )}
            </button>
            
            {openIndex === index && (
              <div style={{
                padding: '0 20px 16px 20px',
                color: theme.colors.text.secondary,
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                {faq.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// MODULE CARD
// ============================================
function ModuleCard({ moduleId, content, onClick }) {
  const { theme } = useTheme();
  const Icon = moduleIcons[moduleId] || HelpCircle;
  
  return (
    <div
      onClick={onClick}
      style={{
        background: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: '12px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.colors.brand.primary;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = theme.colors.border.primary;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{
          background: `${theme.colors.brand.primary}20`,
          borderRadius: '10px',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={24} style={{ color: theme.colors.brand.primary }} />
        </div>
        
        <div style={{ flex: 1 }}>
          <h4 style={{
            margin: '0 0 6px 0',
            fontSize: '15px',
            fontWeight: '600',
            color: theme.colors.text.primary
          }}>
            {content.title}
          </h4>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: theme.colors.text.secondary,
            lineHeight: '1.5',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {content.description}
          </p>
          <span style={{
            display: 'inline-block',
            marginTop: '10px',
            fontSize: '12px',
            color: theme.colors.brand.primary,
            fontWeight: '500'
          }}>
            {content.faqs?.length || 0} FAQ items →
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN HELP PAGE
// ============================================
export default function HelpPage() {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState(null);
  const [isMobile] = useState(window.innerWidth <= 768);
  
  // Filter modules based on search
  const filteredModules = Object.entries(helpContent).filter(([id, content]) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      content.title.toLowerCase().includes(query) ||
      content.description.toLowerCase().includes(query) ||
      content.faqs?.some(faq => 
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      )
    );
  });
  
  // Get random tip
  const randomTip = quickTips[Math.floor(Math.random() * quickTips.length)];
  
  return (
    <div style={{ 
      padding: isMobile ? '20px 16px' : '40px',
      maxWidth: '1000px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: isMobile ? '24px' : '32px',
          fontWeight: '700',
          color: theme.colors.text.primary,
          margin: '0 0 8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <HelpCircle size={isMobile ? 28 : 36} style={{ color: theme.colors.brand.primary }} />
          Help Center
        </h1>
        <p style={{
          color: theme.colors.text.secondary,
          fontSize: '16px',
          margin: 0
        }}>
          Find answers, learn features, and get the most out of Bidoryx
        </p>
      </div>
      
      {/* Search */}
      <div style={{
        position: 'relative',
        marginBottom: '32px'
      }}>
        <Search 
          size={20} 
          style={{ 
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: theme.colors.text.tertiary
          }} 
        />
        <input
          type="text"
          placeholder="Search help topics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '14px 16px 14px 48px',
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: '12px',
            color: theme.colors.text.primary,
            fontSize: '15px',
            outline: 'none'
          }}
        />
      </div>
      
      {/* Quick Tip */}
      <div style={{
        background: `linear-gradient(135deg, ${theme.colors.brand.primary}15 0%, ${theme.colors.brand.primary}05 100%)`,
        border: `1px solid ${theme.colors.brand.primary}30`,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '32px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <Zap size={20} style={{ color: theme.colors.brand.primary, flexShrink: 0 }} />
        <div>
          <span style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: theme.colors.brand.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Quick Tip
          </span>
          <p style={{ 
            margin: '4px 0 0 0', 
            fontSize: '14px', 
            color: theme.colors.text.primary 
          }}>
            {randomTip}
          </p>
        </div>
      </div>
      
      {/* Module Grid or Selected Module */}
      {selectedModule ? (
        <div>
          <button
            onClick={() => setSelectedModule(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.colors.brand.primary,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '20px',
              padding: 0
            }}
          >
            ← Back to all topics
          </button>
          
          <div style={{
            background: theme.colors.bg.secondary,
            borderRadius: '16px',
            padding: '32px',
            border: `1px solid ${theme.colors.border.primary}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <span style={{ fontSize: '40px' }}>{helpContent[selectedModule].icon}</span>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '700',
                  color: theme.colors.text.primary
                }}>
                  {helpContent[selectedModule].title}
                </h2>
              </div>
            </div>
            
            <p style={{
              color: theme.colors.text.secondary,
              fontSize: '15px',
              lineHeight: '1.7',
              marginBottom: '24px'
            }}>
              {helpContent[selectedModule].description}
            </p>
            
            {helpContent[selectedModule].features && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: theme.colors.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  margin: '0 0 12px 0'
                }}>
                  Key Features
                </h3>
                <ul style={{
                  margin: 0,
                  padding: '0 0 0 20px',
                  color: theme.colors.text.primary,
                  fontSize: '14px',
                  lineHeight: '2'
                }}>
                  {helpContent[selectedModule].features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {helpContent[selectedModule].faqs && (
              <FaqAccordion 
                faqs={helpContent[selectedModule].faqs} 
                title="Frequently Asked Questions"
              />
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Module Cards Grid */}
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: theme.colors.text.primary,
            margin: '0 0 16px 0'
          }}>
            Browse by Feature
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '16px',
            marginBottom: '48px'
          }}>
            {filteredModules.map(([id, content]) => (
              <ModuleCard
                key={id}
                moduleId={id}
                content={content}
                onClick={() => setSelectedModule(id)}
              />
            ))}
          </div>
          
          {/* General FAQs */}
          <FaqAccordion 
            faqs={generalFaqs} 
            title="General Questions"
          />
        </>
      )}
      
      {/* Contact Support */}
      <div style={{
        background: theme.colors.bg.secondary,
        borderRadius: '16px',
        padding: '32px',
        border: `1px solid ${theme.colors.border.primary}`,
        textAlign: 'center',
        marginTop: '48px'
      }}>
        <MessageCircle size={40} style={{ color: theme.colors.brand.primary, marginBottom: '16px' }} />
        <h3 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: theme.colors.text.primary,
          margin: '0 0 8px 0'
        }}>
          Still need help?
        </h3>
        <p style={{
          color: theme.colors.text.secondary,
          fontSize: '15px',
          margin: '0 0 24px 0'
        }}>
          Can't find what you're looking for? Our support team is here to help.
        </p>
        
        <a
          href="mailto:support@bidoryx.com"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: theme.colors.brand.primary,
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '600'
          }}
        >
          <Mail size={18} />
          Contact Support
        </a>
      </div>
    </div>
  );
}
